import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, Eye, Heart, Shield } from 'lucide-react';

const IMAGES = {
  hero: "https://images.pexels.com/photos/7195799/pexels-photo-7195799.jpeg?auto=compress&cs=tinysrgb&w=1920",
  team1: "https://images.unsplash.com/photo-1632765866070-3fadf25d3d5b?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  team2: "https://images.unsplash.com/photo-1612214070495-ffc6b69cda4b?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  salon: "https://images.unsplash.com/photo-1759134198561-e2041049419c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8 }
};

const stagger = {
  animate: { transition: { staggerChildren: 0.15 } }
};

const values = [
  {
    icon: Target,
    title: 'Our Mission',
    text: 'To provide exceptional haircare and beauty services that celebrate and enhance the natural beauty of Black and African hair, while creating a welcoming space where every client feels valued and understood.'
  },
  {
    icon: Eye,
    title: 'Our Vision',
    text: 'To be the leading premium unisex salon known for excellence in Black hair care, innovative styling techniques, and a commitment to educating our community about healthy hair practices.'
  },
  {
    icon: Heart,
    title: 'Hair Health',
    text: 'We believe in prioritising the health of your hair above all else. Every service we provide focuses on maintaining and improving the integrity of your natural hair texture.'
  },
  {
    icon: Shield,
    title: 'Customer Care',
    text: 'Your satisfaction and comfort are our top priorities. We take time to understand your unique needs, preferences, and lifestyle to deliver personalised results every time.'
  },
];

export const About = () => {
  return (
    <main data-testid="about-page">
      {/* Page Header */}
      <section 
        className="page-header"
        style={{ 
          backgroundImage: `url(${IMAGES.hero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-[#0A0A0A]" />
        <div className="container-custom relative z-10 text-center pt-24">
          <motion.p 
            className="text-[#C9A227] uppercase tracking-[0.3em] text-sm mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Our Story
          </motion.p>
          <motion.h1 
            className="font-heading text-5xl md:text-6xl lg:text-7xl text-[#F5F5F5]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            About <span className="italic">UNATHI</span>
          </motion.h1>
        </div>
      </section>

      {/* Brand Story Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="story-section">
        <div className="container-custom">
          <motion.div 
            className="grid lg:grid-cols-2 gap-16 items-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <div>
              <motion.div 
                className="w-16 h-[1px] bg-[#C9A227] mb-8"
                variants={fadeInUp}
              />
              <motion.h2 
                className="font-heading text-4xl md:text-5xl text-[#F5F5F5] mb-6"
                variants={fadeInUp}
              >
                More Than Just a <span className="italic">Salon</span>
              </motion.h2>
              <motion.div className="space-y-6 text-[#999] leading-relaxed" variants={fadeInUp}>
                <p>
                  UNATHI by Onyx Hairport was created to provide a premium salon experience 
                  for clients who want more than just a hairstyle — they want care, confidence, 
                  beauty, and excellence.
                </p>
                <p>
                  We understand the uniqueness of Black and African hair and the importance of 
                  working with professionals who respect texture, style preferences, hair health, 
                  and personal expression.
                </p>
                <p>
                  Our salon welcomes both women and men, offering a full range of hair and 
                  grooming services in a clean, stylish, and comfortable setting. From intricate 
                  braiding patterns to precision fades, from natural hair care to wig installation, 
                  we do it all with expertise and passion.
                </p>
              </motion.div>
            </div>

            <motion.div 
              className="grid grid-cols-2 gap-4"
              variants={fadeInUp}
            >
              <div className="img-container aspect-[3/4] rounded-none overflow-hidden">
                <img src={IMAGES.team1} alt="Stylist at work" className="w-full h-full object-cover" />
              </div>
              <div className="img-container aspect-[3/4] rounded-none overflow-hidden mt-12">
                <img src={IMAGES.team2} alt="Barber services" className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-[#F9F9F9]" data-testid="values-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.p 
              className="text-[#C9A227] uppercase tracking-[0.3em] text-sm mb-4"
              variants={fadeInUp}
            >
              What Drives Us
            </motion.p>
            <motion.h2 
              className="font-heading text-4xl md:text-5xl text-[#2E2E2E]"
              variants={fadeInUp}
            >
              Our <span className="italic">Values</span>
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="bg-white p-8 border border-neutral-100 hover:border-[#C9A227]/50 hover:shadow-lg transition-all"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`value-${index}`}
              >
                <div className="w-14 h-14 border border-[#C9A227] flex items-center justify-center mb-6">
                  <value.icon size={24} className="text-[#C9A227]" />
                </div>
                <h3 className="font-heading text-2xl text-[#2E2E2E] mb-4">{value.title}</h3>
                <p className="text-[#666] leading-relaxed">{value.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Salon Experience Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="experience-section">
        <div className="container-custom">
          <motion.div 
            className="grid lg:grid-cols-2 gap-16 items-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div 
              className="img-container aspect-video rounded-none overflow-hidden order-2 lg:order-1"
              variants={fadeInUp}
            >
              <img src={IMAGES.salon} alt="UNATHI salon interior" className="w-full h-full object-cover" />
            </motion.div>

            <div className="order-1 lg:order-2">
              <motion.p 
                className="text-[#C9A227] uppercase tracking-[0.3em] text-sm mb-4"
                variants={fadeInUp}
              >
                The Experience
              </motion.p>
              <motion.h2 
                className="font-heading text-4xl md:text-5xl text-[#F5F5F5] mb-6"
                variants={fadeInUp}
              >
                A Space for <span className="italic">You</span>
              </motion.h2>
              <motion.div className="space-y-6 text-[#999] leading-relaxed" variants={fadeInUp}>
                <p>
                  When you step into UNATHI, you're stepping into more than a salon. 
                  You're entering a space designed with you in mind — where the music feels right, 
                  the atmosphere is welcoming, and the team genuinely cares about your hair journey.
                </p>
                <p>
                  Our stylists and barbers are not just skilled — they're passionate about what 
                  they do. They stay current with the latest techniques, trends, and products to 
                  ensure you always leave looking and feeling your absolute best.
                </p>
                <p>
                  Whether it's your first visit or your hundredth, we treat every appointment 
                  as an opportunity to exceed your expectations.
                </p>
              </motion.div>
              <motion.div className="mt-8" variants={fadeInUp}>
                <Link to="/book" data-testid="about-book-btn" className="btn-primary">
                  Book Your Visit
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#C9A227]" data-testid="about-cta-section">
        <div className="container-custom">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 
              className="font-heading text-4xl md:text-5xl text-[#000] mb-6"
              variants={fadeInUp}
            >
              Ready to Experience the <span className="italic">Difference</span>?
            </motion.h2>
            <motion.p 
              className="text-[#2E2E2E] text-lg mb-10"
              variants={fadeInUp}
            >
              Join the UNATHI family today and discover what premium hair care truly feels like.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link 
                to="/book" 
                data-testid="about-cta-book-btn" 
                className="btn-primary bg-[#000] text-[#F5F5F5] hover:bg-[#2E2E2E]"
              >
                Book Appointment
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default About;
